import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const BG = "https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMGJsdWUlMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3ODczMzY0NTF8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const attempt = async () => { await login(email, password, remember); navigate("/"); };
    try {
      await attempt();
    } catch (err) {
      const status = err?.response?.status;
      const warmingUp = !err?.response || status === 502 || status === 503 || status === 504 || status >= 500;
      if (warmingUp) {
        // Cold start: backend not ready yet → inform and retry once.
        setError("Il servizio si sta avviando, riprovo tra qualche secondo...");
        await new Promise((r) => setTimeout(r, 4000));
        try {
          await attempt();
          setError("");
          setLoading(false);
          return;
        } catch (err2) {
          setError(err2?.response?.status ? apiError(err2) : "Servizio momentaneamente non disponibile. Riprova tra qualche istante.");
        }
      } else {
        setError(apiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-[#0b1228] via-[#14214c] to-[#0d1730]">
      {/* orizzonte: alone luminoso contenuto, in basso al centro */}
      <div className="pointer-events-none absolute left-1/2 bottom-[-9rem] h-[22rem] w-[22rem] -translate-x-1/2 rounded-full blur-[90px]"
        style={{ background: "radial-gradient(circle at 50% 40%, rgba(103,232,249,0.32), rgba(37,99,235,0.16) 52%, transparent 70%)" }} />
      <div className="pointer-events-none absolute -top-28 -left-20 h-80 w-80 rounded-full bg-blue-600/25 blur-[110px] animate-float-slow" />
      <div className="pointer-events-none absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-indigo-500/20 blur-[110px] animate-float" />

      <div className="relative z-10 min-h-screen max-w-6xl mx-auto px-6 sm:px-10 py-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 lg:gap-16">
        <div className="animate-fade-up max-w-lg">
          <img src="/synesis-logo-mark-light.png" alt="Synesis AI" className="h-9 sm:h-11 w-auto object-contain [filter:drop-shadow(0_0_22px_rgba(34,211,238,0.28))]" />
          <h2 className="mt-5 font-display text-2xl sm:text-3xl xl:text-4xl font-bold tracking-tight text-white leading-[1.18]">
            Tutta la documentazione dei tuoi sistemi di gestione, <span className="text-cyan-300">in un unico click.</span>
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-md leading-relaxed">
            Schedatura assistita da AI, copertura requisiti e agenda scadenze per ogni cliente.
          </p>
          <p className="mt-5 text-xs tracking-wide text-slate-500">
            © 2026 <span className="font-semibold text-slate-400">Caterina Valentini</span> — Management Systems Consultant
          </p>
        </div>

        <div className="w-full max-w-sm lg:shrink-0 animate-fade-up rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-xl p-6 sm:p-7 shadow-[0_30px_70px_-35px_rgba(0,0,0,0.9)]">
          <span className="label-caps">Accesso</span>
          <h2 className="font-display text-3xl font-semibold tracking-tight mt-1 mb-7 text-white">Bentornato</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPassword ? "text" : "password"} value={password} data-testid="login-password"
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="pr-10" />
                <button type="button" tabIndex={-1} data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-600" role="alert" data-testid="login-error">{error}</p>}
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
              Ricordami e mantieni l'accesso
            </label>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white shadow-lg shadow-blue-900/40">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Accedi
            </Button>
          </form>
          <p className="text-xs text-slate-500 mt-6">
            Gli utenti cliente accedono tramite invito ricevuto via email.
          </p>
        </div>
      </div>
    </div>
  );
}
